<template>
  <div class="bg-white dark:bg-gray-900 rounded-lg shadow hover:shadow-md transition-shadow overflow-hidden">
    <div class="p-4">
      <!-- Header -->
      <div class="flex items-start justify-between gap-3 mb-3">
        <div class="flex-1 min-w-0">
          <h3 class="text-base font-semibold text-gray-900 dark:text-white truncate">
            {{ group.name }}
          </h3>
          <p
            v-if="group.description"
            class="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2"
          >
            {{ group.description }}
          </p>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <span
            class="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium"
            :class="group.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-200' : 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'"
          >
            {{ group.is_active ? 'Activo' : 'Inactivo' }}
          </span>
          
          <!-- Action dropdown -->
          <DropdownMenu
            :id="`modifier-group-${group.id}`"
            button-label="Group actions"
            width="md"
          >
            <DropdownMenuItem
              :icon="PencilIcon"
              variant="default"
              @click="$emit('edit', group)"
            >
              Editar
            </DropdownMenuItem>
            
            <DropdownMenuItem
              :icon="ArrowPathIcon"
              variant="default"
              :is-loading="isLoading"
              @click="$emit('toggle', group)"
            >
              {{ group.is_active ? 'Desactivar' : 'Activar' }}
            </DropdownMenuItem>
            
            <DropdownMenuDivider />
            
            <DropdownMenuItem
              :icon="TrashIcon"
              variant="danger"
              @click="$emit('delete', group)"
            >
              Eliminar
            </DropdownMenuItem>
          </DropdownMenu>
        </div>
      </div>

      <!-- Info badges -->
      <div class="flex flex-wrap gap-2 mb-3">
        <span class="inline-flex items-center px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs">
          {{ group.options?.length || 0 }} opciones
        </span>
        <span class="inline-flex items-center px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs">
          {{ group.min_select_default ?? 0 }}-{{ group.max_select_default ?? 1 }}
        </span>
        <span
          v-if="group.required_default"
          class="inline-flex items-center px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-200 text-xs"
        >
          Requerido
        </span>
      </div>

      <!-- Options preview -->
      <div
        v-if="group.options && group.options.length > 0"
        class="mb-3"
      >
        <div class="flex flex-wrap gap-1">
          <span
            v-for="option in group.options.slice(0, 3)"
            :key="option.id || option.name"
            class="inline-flex px-2 py-1 text-xs rounded bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600"
          >
            {{ option.name }}
            <span
              v-if="option.price_delta && option.price_delta > 0"
              class="ml-1 text-gray-500"
            >
              +${{ option.price_delta }}
            </span>
          </span>
          <span
            v-if="group.options.length > 3"
            class="inline-flex px-2 py-1 text-xs rounded bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600"
          >
            +{{ group.options.length - 3 }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ModifierGroup } from '@/types/menu';
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';
import DropdownMenuDivider from '@/components/ui/DropdownMenuDivider.vue';
import { PencilIcon, ArrowPathIcon, TrashIcon } from '@heroicons/vue/24/outline';

defineProps<{
  group: ModifierGroup;
  isLoading?: boolean;
}>();

defineEmits<{
  edit: [group: ModifierGroup];
  toggle: [group: ModifierGroup];
  delete: [group: ModifierGroup];
}>();
</script>
